import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download, FileText, Search, CreditCard } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Button from "../../components/Button/Button";
import APITable from "../../components/Tables/APITable";
import Pagination from "../../components/Pagination/Pagination";
import Input from "../../components/Input/Input";
import DateRangePicker from "../../components/Calender/DateRangePicker";
import {
  BillingCardSkeleton,
  TableSkeleton,
} from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";
import { can } from "../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./Billing.module.scss";

const Billing = () => {
  const navigate = useNavigate();

  /* ===================== TAB ===================== */
  const [activeTab, setActiveTab] = useState("invoices");

  /* ===================== TABLE ===================== */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skeletonLoading, setSkeletonLoading] = useState(true);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [, setSelectedCompany] = useState(null);

  /* ===================== PAGINATION ===================== */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  const [dashboard, setDashboard] = useState({
    balance: 0,
    apiCall: 0,
    usageAmount: 0,
    avgUsageAmount: 0,
  });

  /* ===================== SEARCH ===================== */
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  /* ===================== DATE FILTER ===================== */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);

  /* ===================== RESET FILTER ===================== */
  const resetFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setFromDate("");
    setToDate("");
    setPage(1);
    setDatePickerKey((prev) => prev + 1);
  };

  /* ===================== COLUMNS ===================== */
  const invoiceColumns = [
    { label: "Invoice ID", key: "invoiceId" },
    { label: "Date", key: "date" },
    { label: "API Credits", key: "add_credits" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
    { label: "Action", key: "action" },
  ];

  const apiLogColumns = [
    { label: "Transaction ID", key: "transaction_no" },
    { label: "Date & Time", key: "created_at" },
    { label: "API", key: "client_api_name" },
    { label: "Key Name", key: "key_name" },
    { label: "Credit Consumption", key: "credit_points_consumed" },
    { label: "Balance", key: "balance" },
  ];

  /* ===================== API CALL ===================== */
  const fetchBillingData = useCallback(async () => {
    if (activeTab === "invoices") {
      setInvoiceLoading(true);
    } else {
      setLogsLoading(true);
    }

    try {
      const query = new URLSearchParams({
        page,
        limit,
        ...(fromDate &&
          toDate && {
            from_date: fromDate,
            to_date: toDate,
          }),
        ...(appliedSearch && { search: appliedSearch }),
        ...(activeTab === "invoices" && { status: "Paid" }),
      }).toString();

      const endpoint =
        activeTab === "invoices" ? `/pay?${query}` : `/api-logs?${query}`;

      const res = await apiGet(
        activeTab === "invoices" ? "billing" : "catalog",
        endpoint,
      );

      if (res?.success) {
        if (activeTab === "invoices") {
          const formatted = res.data.map((item) => ({
            id: item.id,
            invoiceId: item.invoice_no,
            date: item.invoice_date.split("T")[0],
            add_credits: item.add_credits || "-",
            amount: `₹${item.total_amount}`,
            status: item.status,
            action: "view",
            company_detail: item.company_detail,
          }));

          setRows(formatted);
          if (res.data.length > 0) {
            setSelectedCompany(res.data[0].company_detail);
          }
        } else {
          setRows(res.data || []);
        }

        setPagination({
          total: res.pagination?.total || 0,
          totalPages: res.pagination?.totalPages || 1,
        });
      }
    } catch (error) {
      console.error("Billing API Error:", error);
    } finally {
      if (activeTab === "invoices") {
        setInvoiceLoading(false);
      } else {
        setLogsLoading(false);
      }
    }
  }, [activeTab, page, limit, fromDate, toDate, appliedSearch]);

  const fetchBalance = async () => {
    try {
      const res = await apiGet("catalog", "/apimaster/balance");
      if (res?.status === "success") {
        setDashboard((prev) => ({
          ...prev,
          balance: Number(res.data?.balance || 0),
        }));
      }
    } catch (err) {
      console.error("Balance fetch error", err);
    }
  };

  const fetchBillingDashboard = async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/api-usage-billing");
      if (res?.status === "success") {
        setDashboard((prev) => ({
          ...prev,
          apiCall: Number(res.data?.api_call || 0),
          usageAmount: Number(res.data?.usage_amount || 0),
          avgUsageAmount: Number(res.data?.avg_usage_amount || 0),
        }));
      }
    } catch (err) {
      console.error("Billing dashboard error", err);
    } finally {
      setLoading(false);
    }
  };

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    fetchBalance();
    fetchBillingDashboard();
    fetchBillingData();
  }, [fetchBillingData]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, fromDate, toDate]);

  useEffect(() => {
    setSkeletonLoading(false);
  }, []);

  /* ===================== CSV DOWNLOAD ===================== */
  const handleDownload = () => {
    const cols = activeTab === "invoices" ? invoiceColumns : apiLogColumns;
    const header = cols.map((c) => c.label).join(",") + "\n";
    const body = rows
      .map((r) => cols.map((c) => r[c.key] ?? "").join(","))
      .join("\n");

    const blob = new Blob([header + body], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = activeTab === "invoices" ? "invoices.csv" : "api_logs.csv";
    link.click();
  };

  /* ===================== JSX ===================== */
  return (
    <div className={styles["admin-billing__page"]}>
      {/* HEADER */}
      <div className={styles["admin-billing__header"]}>
        <div className={styles["admin-billing__icon-wrapper"]}>
          <CreditCard size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-billing__title-group"]}>
          <h2 className={styles["admin-billing__title"]}>Billing</h2>
          <p className={styles["admin-billing__subtitle"]}>
            Manage your credits, invoices, and transaction history
          </p>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className={styles["admin-billing__summary-row"]}>
        {loading || skeletonLoading ? (
          [...Array(3)].map((_, i) => <BillingCardSkeleton key={i} />)
        ) : (
          <>
            <div
              className={`${styles["admin-billing__card"]} ${styles["admin-billing__card--blue"]}`}
            >
              <p className={styles["admin-billing__card-label"]}>
                Current Balance
              </p>
              <h3 className={styles["admin-billing__card-value"]}>
                {dashboard.balance.toLocaleString()}
              </h3>
              {/* Org-admin credit purchase flow → "purchase-credits" action */}
              {can("billing", "purchase-credits") && (
                <button
                  className={styles["admin-billing__btn-white"]}
                  onClick={() => navigate("/billing/pricing")}
                >
                  <Plus size={16} strokeWidth={3} /> Add Credits
                </button>
              )}
            </div>

            <div className={styles["admin-billing__card"]}>
              <p className={styles["admin-billing__card-label"]}>
                This Month Usage
              </p>
              <h3 className={styles["admin-billing__card-value"]}>
                {dashboard.usageAmount.toLocaleString()}
              </h3>
              <span className={styles["admin-billing__card-subtext"]}>
                {dashboard.apiCall.toLocaleString()} API calls
              </span>
            </div>

            <div className={styles["admin-billing__card"]}>
              <p className={styles["admin-billing__card-label"]}>
                Average Monthly Spend
              </p>
              <h3 className={styles["admin-billing__card-value"]}>
                {dashboard.avgUsageAmount.toLocaleString()}
              </h3>
              <span className={styles["admin-billing__card-subtext"]}>
                Last 3 months
              </span>
            </div>
          </>
        )}
      </div>

      {/* TABLE SECTION */}
      <div className={styles["admin-billing__table-card"]}>
        {/* Top Controls: Tabs & Search */}
        <div className={styles["admin-billing__controls-row"]}>
          <div className={styles["admin-billing__tabs"]}>
            <button
              className={`${styles["admin-billing__tab"]} ${
                activeTab === "invoices"
                  ? styles["admin-billing__tab--active"]
                  : ""
              }`}
              onClick={() => {
                setActiveTab("invoices");
                resetFilters();
              }}
            >
              Recent Invoices
            </button>

            <button
              className={`${styles["admin-billing__tab"]} ${
                activeTab === "logs" ? styles["admin-billing__tab--active"] : ""
              }`}
              onClick={() => {
                setActiveTab("logs");
                resetFilters();
              }}
            >
              API Logs
            </button>
          </div>

          <div className={styles["admin-billing__search-box"]}>
            <Input
              type="search"
              placeholder={
                activeTab === "invoices" ? "Search Invoice ID" : "Search API"
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedSearch(search);
                  setPage(1);
                }
              }}
            />
            <Button
              text="Search"
              icon={Search}
              onClick={() => {
                setAppliedSearch(search);
                setPage(1);
              }}
            />
          </div>
        </div>

        {/* Secondary Controls: Info & Download */}
        <div className={styles["admin-billing__action-row"]}>
          <div className={styles["admin-billing__showing-text"]}>
            Showing {rows.length} of {pagination.total}
          </div>

          <div className={styles["admin-billing__filters"]}>
            <DateRangePicker
              key={datePickerKey}
              maxDate={new Date()}
              onChange={(type, value) => {
                if (type === "from") {
                  setFromDate(value);
                  setPage(1);
                }
                if (type === "to") {
                  setToDate(value);
                  setPage(1);
                }
              }}
            />
            <button
              className={styles["admin-billing__download-btn"]}
              onClick={handleDownload}
            >
              <Download size={16} strokeWidth={2.5} />
              Download
            </button>
          </div>
        </div>

        {/* The Table */}
        <div className={styles["admin-billing__table-wrapper"]}>
          {activeTab === "invoices" && invoiceLoading ? (
            <TableSkeleton columns={6} rows={4} />
          ) : activeTab === "logs" && logsLoading ? (
            <TableSkeleton columns={6} rows={3} />
          ) : (
            <APITable
              columns={
                activeTab === "invoices" ? invoiceColumns : apiLogColumns
              }
              data={rows}
              renderCell={(row, key) => {
                /* DATE & TIME LOGIC */
                if (activeTab === "logs" && key === "created_at") {
                  const value = row.created_at;
                  if (!value) return "---";
                  const [date, time, meridian] = value.split(" ");
                  return (
                    <span>
                      {date}{" "}
                      <span className={styles["admin-billing__time-color"]}>
                        {time} {meridian}
                      </span>
                    </span>
                  );
                }

                /* STATUS BADGES */
                if (activeTab === "invoices" && key === "status") {
                  const isPaid = row.status === "Paid";
                  return (
                    <span
                      className={`${styles["admin-billing__badge"]} ${
                        isPaid
                          ? styles["admin-billing__badge--paid"]
                          : styles["admin-billing__badge--pending"]
                      }`}
                    >
                      {row.status}
                    </span>
                  );
                }

                /* ACTION CELL (INVOICE DOWNLOAD) */
                if (activeTab === "invoices" && key === "action") {
                  return (
                    <Link
                      to={`/billing/view/${row.id}`}
                      className={styles["admin-billing__icon-btn"]}
                      title="Download Invoice"
                    >
                      <FileText size={18} strokeWidth={2.5} />
                    </Link>
                  );
                }

                const value = row[key];
                return value !== null && value !== undefined && value !== ""
                  ? value
                  : "---";
              }}
            />
          )}
        </div>

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default Billing;
